import { useSOP } from '@hakimo-ui/hakimo/data-access';
import { Situation, SituationColor } from '@hakimo-ui/hakimo/types';
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { ReactNode, useState } from 'react';
import { PANEL_TITLE_CLASS, Panel } from '../Panel';
import { SituationChip } from './SituationChip';

interface Props {
  locationId: number | null;
}

interface SectionProps {
  title: string;
  isEmpty: boolean;
  emptyText: string;
  children: ReactNode;
}

const ICON_TILE_CLASS =
  'border-onlight-line-2 dark:border-ondark-line-2 dark:hover:bg-ondark-bg-3 hover:bg-onlight-bg-2 text-onlight-text-2 dark:text-ondark-text-2 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border';

function CollapsibleSection({
  title,
  isEmpty,
  emptyText,
  children,
}: SectionProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className="dark:border-ondark-line-2 border-onlight-line-2 border-t">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-3 text-left"
      >
        <span className={PANEL_TITLE_CLASS}>{title}</span>
        <span className={ICON_TILE_CLASS}>
          <ChevronDownIcon
            className={`h-4 w-4 transition-transform ${
              open ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>
      {open && (
        <div className="pb-3">
          {isEmpty ? (
            <p className="text-onlight-text-3 dark:text-ondark-text-2 text-sm">
              {emptyText}
            </p>
          ) : (
            children
          )}
        </div>
      )}
    </div>
  );
}

function toSituation(situation: Situation | string): Situation {
  return typeof situation === 'string'
    ? { label: situation, color: SituationColor.BLUE }
    : situation;
}

export function SopPanel({ locationId }: Props) {
  const { data: sop } = useSOP(
    { locationId: String(locationId ?? '') },
    undefined,
    !!locationId
  );

  const workflow = sop?.sop_workflow;
  const scanSop = sop?.scan_sop;
  const escalationProtocol = workflow?.escalationProtocol ?? [];
  const situations = (
    workflow?.situations ?? [
      ...(scanSop?.emergencySituations ?? []),
      ...(scanSop?.nonEmergencySituations ?? []),
    ]
  )
    .map(toSituation)
    .filter((situation) => situation.label);

  return (
    <Panel className="!py-0">
      <div className="flex items-center justify-between py-3">
        <h2 className={PANEL_TITLE_CLASS}>SOP</h2>
        {locationId !== null && (
          <a
            href={`/locations/${locationId}?tab=sop`}
            target="_blank"
            rel="noreferrer"
            aria-label="Open full SOP"
            className={ICON_TILE_CLASS}
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        )}
      </div>
      <CollapsibleSection
        title="Escalation Protocol"
        isEmpty={escalationProtocol.length === 0}
        emptyText="No escalation protocol configured for this site."
      >
        <ul className="text-onlight-text-2 dark:text-ondark-text-2 list-disc space-y-1 pl-5 text-sm">
          {escalationProtocol.map((step, idx) => (
            <li key={`escalation-${idx}`}>{step}</li>
          ))}
        </ul>
      </CollapsibleSection>
      <CollapsibleSection
        title="Situations"
        isEmpty={situations.length === 0}
        emptyText="No situations configured for this site."
      >
        <div className="flex flex-wrap gap-2">
          {situations.map((situation, idx) => (
            <SituationChip key={`situation-${idx}`} situation={situation} />
          ))}
        </div>
      </CollapsibleSection>
    </Panel>
  );
}

export default SopPanel;
